import { useCallback, useEffect, useRef } from "react";

import { EventOrigin, Events, MeasureMode, SectionMode, track } from "apis/procore";
import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { useIsolationVisibility } from "components/window/core/objectTree/hooks/useIsolationVisibility";
import { FeatureToggle } from "config/featureToggles";
import { useDispatchHidden, useLazyHidden } from "contexts/hidden";
import {
    HighlightCollection,
    useDispatchHighlightCollections,
    useLazyHighlightCollections,
} from "contexts/highlightCollections";
import { useDispatchHighlighted, useLazyHighlighted } from "contexts/highlighted";
import { useDispatchSelectionBasket } from "contexts/selectionBasket";
import { CORE_TOOLBAR_IDS } from "contexts/toolbars";
import { CORE_WINDOW_IDS, useDispatchWindows, windowsActions } from "contexts/windows";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import { useResetView } from "features/home/useResetView";
import { Picker, selectMainObject, selectMainObjects, selectPlainIsolationActive } from "features/render";
import { useSetController } from "features/render/hooks/useSetController";
import { useToggleXRayMode } from "features/render/hooks/useToggleXRayMode";
import { useEnterToolMode } from "features/toolMode/useEnterToolMode";
import { runRedo, runUndo } from "features/undoRedo";
import {
    selectHotkeysEnabled,
    selectIsAxisSphereVisible,
    selectIsMiniMapVisible,
    selectIsProcoreDesign,
    selectObjectMeasureEnabled,
} from "slices/explorer";
import { featureTogglesActions } from "slices/featureToggles";
import { selectActiveToolMode } from "slices/procoreUx";
import { ProcoreToolMode } from "slices/procoreUx/types";
import { hideObjects, showObjects, unhideLastHiddenObjects, unselectAll } from "utils/objectActions";

import { useConfirmCurrentModeChanges } from "../features/toolMode/useConfirmCurrentModeChanges";
import { useHandlePickerEsc } from "./useHandlePickerEsc";

// If shortcut requires multiple sequential key presses -
// then need to be done within this delay
export const MULTIPLE_PRESS_DELAY = 1000;

function hasModifierKey(event: KeyboardEvent) {
    return event.ctrlKey || event.metaKey || event.altKey;
}

export function useGlobalShortcuts() {
    const store = useAppStore();
    const confirmCurrentModeChanges = useConfirmCurrentModeChanges();
    const handlePickerEsc = useHandlePickerEsc();
    const resetView = useResetView();
    const setController = useSetController();
    const { flyToSelected } = useFlyToSelected();
    const dispatch = useAppDispatch();
    const lazyHighlighted = useLazyHighlighted();
    const lazyHidden = useLazyHidden();
    const lazyHighlightCollections = useLazyHighlightCollections();

    const dispatchHidden = useDispatchHidden();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const dispatchWindows = useDispatchWindows();

    const previousPressedKey = useRef<PressedKey | null>(null);
    const previousPressedKeyTimeout = useRef<NodeJS.Timeout | null>(null);
    const hotkeysEnabled = useAppSelector(selectHotkeysEnabled);

    const enterToolMode = useEnterToolMode();
    const toggleXRayMode = useToggleXRayMode();
    const isolation = useIsolationVisibility();

    const undo = useCallback(() => {
        runUndo(dispatch, store.getState());
    }, [store, dispatch]);
    const redo = useCallback(() => {
        runRedo(dispatch, store.getState());
    }, [store, dispatch]);

    useEffect(() => {
        if (!hotkeysEnabled) {
            return;
        }

        function rememberPressedKey(event: KeyboardEvent) {
            previousPressedKey.current = {
                ctrl: event.ctrlKey,
                meta: event.metaKey,
                alt: event.altKey,
                shift: event.shiftKey,
                code: event.code,
            };
            if (previousPressedKeyTimeout.current) {
                clearTimeout(previousPressedKeyTimeout.current);
            }
            previousPressedKeyTimeout.current = setTimeout(() => {
                previousPressedKey.current = null;
                previousPressedKeyTimeout.current = null;
            }, MULTIPLE_PRESS_DELAY);
        }

        function resetMemorizedPressedKey() {
            if (previousPressedKeyTimeout.current) {
                clearTimeout(previousPressedKeyTimeout.current);
                previousPressedKeyTimeout.current = null;
            }
            previousPressedKey.current = null;
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (["INPUT", "TEXTAREA"].includes((event.target as HTMLElement).tagName)) {
                return;
            }

            const state = store.getState();
            const isProcoreDesign = selectIsProcoreDesign(state);
            const mainObject = selectMainObject(state);
            const mainObjects = selectMainObjects(state);
            const isAxisSphereVisible = selectIsAxisSphereVisible(state);
            const isMiniMapVisible = selectIsMiniMapVisible(state);
            const activeToolMode = selectActiveToolMode(state);
            const isObjectMeasureEnabled = selectObjectMeasureEnabled(state);
            const isStandalone = !isProcoreDesign;

            const highlighted = lazyHighlighted.current.idArr;
            const { ids: hiddenIdsMap, idArr: hidden, lastHiddenIds } = lazyHidden.current;
            const {
                secondaryHighlight: { idArr: currentSecondaryHighlightIds },
            } = lazyHighlightCollections.current;

            switch (event.code) {
                case "KeyH": {
                    if (event.ctrlKey || event.metaKey) {
                        event.preventDefault();
                        const hasSelected = highlighted.length > 0 || mainObject !== undefined;
                        const selectedIds =
                            mainObject !== undefined && !highlighted.includes(mainObject)
                                ? [...highlighted, mainObject]
                                : highlighted;
                        if (hasSelected) {
                            if (isProcoreDesign) {
                                const isIsolationActive = selectPlainIsolationActive(state);

                                if (isIsolationActive) {
                                    // Hidden during isolation means absent from the keep-set
                                    // (or also listed in hidden.ids). The eye icon's helper
                                    // owns the keep-set update, the snapshot, and undo.
                                    const keepSet =
                                        lazyHighlightCollections.current[HighlightCollection.TransparentSelectionBase]
                                            ?.ids;
                                    const areAllIsolationHidden = selectedIds.every(
                                        (id: number) => !keepSet?.has(id) || hiddenIdsMap.has(id),
                                    );
                                    isolation.setIdsVisible(selectedIds, areAllIsolationHidden);
                                    void track(
                                        areAllIsolationHidden ? Events.bimObjects.shown : Events.bimObjects.hidden,
                                        { origin: EventOrigin.Keyboard },
                                    );
                                    return;
                                }

                                const areAllSelectedHidden = selectedIds.every((id: number) => hiddenIdsMap.has(id));
                                if (areAllSelectedHidden) {
                                    showObjects(selectedIds, {
                                        dispatch,
                                        dispatchHidden,
                                    });
                                    void track(Events.bimObjects.shown, { origin: EventOrigin.Keyboard });
                                } else {
                                    hideObjects(highlighted, {
                                        dispatch,
                                        dispatchHighlighted,
                                        dispatchHidden,
                                        dispatchSelectionBasket,
                                        dispatchHighlightCollections,
                                        currentSecondaryHighlightIds,
                                        currentMainObject: mainObject,
                                        currentMainObjects: mainObjects,
                                        currentHiddenIds: hiddenIdsMap,
                                        currentLastHiddenIds: lastHiddenIds,
                                        preserveSelection: true,
                                        includeMainObjectToHide: true,
                                    });
                                    void track(Events.bimObjects.hidden, { origin: EventOrigin.Keyboard });
                                }
                            } else {
                                hideObjects(highlighted, {
                                    dispatch,
                                    dispatchHighlighted,
                                    dispatchHidden,
                                    dispatchSelectionBasket,
                                    dispatchHighlightCollections,
                                    currentSecondaryHighlightIds,
                                    currentMainObject: mainObject,
                                    currentMainObjects: mainObjects,
                                    currentHiddenIds: hiddenIdsMap,
                                    currentLastHiddenIds: lastHiddenIds,
                                    includeMainObjectToHide: true,
                                });
                                void track(Events.bimObjects.hidden, { origin: EventOrigin.Keyboard });
                            }
                        } else if (hidden.length) {
                            showObjects(hidden, {
                                dispatch,
                                dispatchHidden,
                            });
                            void track(Events.bimObjects.shown, { origin: EventOrigin.Keyboard });
                        }
                        return;
                    }
                    if (event.shiftKey) {
                        showObjects(hidden, {
                            dispatch,
                            dispatchHidden,
                        });
                        return;
                    }
                    if (isStandalone && pressedKeyIs(previousPressedKey.current, "KeyH")) {
                        resetMemorizedPressedKey();
                        resetView();
                        void track(Events.toolbar.homeTapped, { origin: EventOrigin.Keyboard });
                        return;
                    }
                    break;
                }
                case "KeyV": {
                    if (hasModifierKey(event)) {
                        return;
                    }
                    setController("flight");
                    void track(Events.toolbar.navigationDefaultTapped, { origin: EventOrigin.Keyboard });
                    break;
                }
                case "KeyF": {
                    if (hasModifierKey(event)) {
                        return;
                    }
                    setController("hover");
                    void track(Events.toolbar.navigationFlyTapped, { origin: EventOrigin.Keyboard });
                    break;
                }
                case "KeyO": {
                    if (hasModifierKey(event)) {
                        if (isProcoreDesign && event.altKey) {
                            event.preventDefault();
                            event.stopPropagation();
                            dispatchWindows(
                                windowsActions.toggleWindow(CORE_WINDOW_IDS.OBJECT_TREE, {
                                    elementId: CORE_TOOLBAR_IDS.DETAILS,
                                    placement: "right",
                                }),
                            );
                            void track(Events.toolbar.objectTreeTapped, { origin: EventOrigin.Keyboard });
                        }
                        return;
                    }
                    setController("cadMiddlePan");
                    void track(Events.toolbar.navigationOrbitTapped, { origin: EventOrigin.Keyboard });
                    break;
                }
                case "Space": {
                    if (hasModifierKey(event) || isProcoreDesign) {
                        // In Procore mode, Space is owned by useSpacebarPan.
                        return;
                    }
                    flyToSelected();
                    void track(Events.bimObjects.zoomedToSelection, { origin: EventOrigin.Keyboard });
                    break;
                }
                case "KeyL": {
                    if (hasModifierKey(event)) {
                        return;
                    }
                    if (isStandalone) {
                        enterToolMode({ picker: Picker.OutlineLaser });
                        void track(Events.measure.enabled, { mode: MeasureMode.Laser, origin: EventOrigin.Keyboard });
                    }
                    break;
                }
                case "KeyC": {
                    if (hasModifierKey(event)) {
                        return;
                    }
                    if (isProcoreDesign && activeToolMode === ProcoreToolMode.Measure) {
                        enterToolMode({ picker: Picker.Coordinates });
                        void track(Events.measure.enabled, {
                            mode: MeasureMode.Coordinates,
                            origin: EventOrigin.Keyboard,
                        });
                    }
                    break;
                }
                case "KeyD": {
                    if (hasModifierKey(event)) {
                        return;
                    }
                    if (activeToolMode === ProcoreToolMode.Measure && isObjectMeasureEnabled) {
                        enterToolMode({ picker: Picker.ObjectMeasurement });
                    }
                    break;
                }
                case "KeyP": {
                    if (hasModifierKey(event)) {
                        if (isProcoreDesign && event.altKey) {
                            event.preventDefault();
                            event.stopPropagation();
                            dispatchWindows(
                                windowsActions.toggleWindow(CORE_WINDOW_IDS.PROPERTIES, {
                                    elementId: CORE_TOOLBAR_IDS.DETAILS,
                                    placement: "right",
                                }),
                            );
                            void track(Events.toolbar.propertiesPanelTapped, { origin: EventOrigin.Keyboard });
                        }
                        return;
                    }
                    if (isStandalone) {
                        enterToolMode({ picker: Picker.PointLine });
                        void track(Events.measure.enabled, {
                            mode: MeasureMode.PointToPoint,
                            origin: EventOrigin.Keyboard,
                        });
                        return;
                    }
                    break;
                }
                case "KeyZ": {
                    if (isProcoreDesign) {
                        if (event.metaKey && event.shiftKey) {
                            // Cmd+Shift+Z

                            // Prevent associated browser keybind. In Chrome on
                            // Mac this is Redo (e.g. for input fields). May
                            // need to refine this handler further to allow for
                            // browser undo/redo in input fields.
                            event.preventDefault();

                            redo();
                            void track(Events.toolbar.redoTapped, { origin: EventOrigin.Keyboard });
                        } else if ((event.metaKey || event.ctrlKey) && !event.shiftKey) {
                            // Cmd+Z / Ctrl+Z, but not Cmd+Shift+Z / Ctrl+Shift+Z

                            // Prevent associated browser keybind. In Chrome on
                            // Mac this is Undo (e.g. for input fields). May
                            // need to refine this handler further to allow for
                            // browser undo/redo in input fields.
                            event.preventDefault();

                            undo();
                            void track(Events.toolbar.undoTapped, { origin: EventOrigin.Keyboard });
                        }
                    }
                    break;
                }
                case "KeyY": {
                    if (isProcoreDesign) {
                        if (event.metaKey || event.ctrlKey) {
                            // Cmd+Y / Ctrl+Y

                            // Prevent associated browser keybind. In Chrome on
                            // Mac this opens history.
                            event.preventDefault();

                            redo();
                            void track(Events.toolbar.redoTapped, { origin: EventOrigin.Keyboard });
                        }
                    }
                    break;
                }
                case "KeyR": {
                    if (isProcoreDesign && (event.ctrlKey || event.metaKey)) {
                        event.preventDefault();
                        event.stopPropagation();
                        resetView({ resetCamera: false });
                        return;
                    }
                    // if (isProcoreDesign && !hasModifierKey(event)) {
                    //     dispatch(renderActions.setPicker(Picker.Dimension));
                    //     void track(Events.measure.enabled, {
                    //         mode: MeasureMode.Dimension,
                    //         origin: EventOrigin.Keyboard,
                    //     });
                    //     return;
                    // }
                    break;
                }
                case "KeyM": {
                    if (hasModifierKey(event)) {
                        return;
                    }
                    if (isProcoreDesign) {
                        enterToolMode({ picker: Picker.PointLine });
                        void track(Events.measure.enabled, {
                            mode: MeasureMode.PointToPoint,
                            origin: EventOrigin.Keyboard,
                        });
                        event.preventDefault();
                        resetMemorizedPressedKey();
                        return;
                    }
                    break;
                }
                case "KeyX": {
                    if (isProcoreDesign && (event.metaKey || event.ctrlKey)) {
                        // Cmd+X / Ctrl+X - Toggle X-Ray mode
                        event.preventDefault();
                        toggleXRayMode();
                        return;
                    }
                    if (hasModifierKey(event)) {
                        return;
                    }
                    if (isProcoreDesign) {
                        enterToolMode({ picker: Picker.SectionPlane });
                        void track(Events.clipPlane.enabled, {
                            mode: SectionMode.ClipPlane,
                            origin: EventOrigin.Keyboard,
                        });
                        event.preventDefault();
                        resetMemorizedPressedKey();
                        return;
                    }
                    break;
                }
                case "Backslash": {
                    if (isProcoreDesign) {
                        if (event.altKey) {
                            event.preventDefault();
                            dispatchWindows(windowsActions.toggleWindow(CORE_WINDOW_IDS.SETTINGS));
                            void track(Events.toolbar.settingsTapped, { origin: EventOrigin.Keyboard });
                        } else if (event.ctrlKey || event.metaKey) {
                            event.preventDefault();
                            // If at least one of the elements is visible - hide them all
                            const isSomeVisible = isAxisSphereVisible || isMiniMapVisible;
                            const visible = !isSomeVisible;
                            dispatch(
                                featureTogglesActions.setFeatureToggles(
                                    visible,
                                    FeatureToggle.AxisSphereVisible,
                                    FeatureToggle.MiniMapVisible,
                                ),
                            );
                        }
                    }
                    break;
                }
                case "Home": {
                    if (isProcoreDesign) {
                        event.preventDefault();
                        event.stopPropagation();
                        resetView();
                        void track(Events.toolbar.homeTapped, { origin: EventOrigin.Keyboard });
                    }
                    break;
                }
                case "Digit0": {
                    if (isProcoreDesign && (event.ctrlKey || event.metaKey)) {
                        event.preventDefault();
                        event.stopPropagation();
                        resetView();
                        void track(Events.toolbar.homeTapped, { origin: EventOrigin.Keyboard });
                    }
                    break;
                }
                case "KeyU": {
                    if (event.ctrlKey || event.metaKey) {
                        event.preventDefault();
                        event.stopPropagation();
                        unhideLastHiddenObjects(lastHiddenIds, {
                            dispatch,
                            dispatchHidden,
                        });
                    }
                    break;
                }
                case "Escape": {
                    if (handlePickerEsc()) {
                        break;
                    }
                    if (isProcoreDesign) {
                        unselectAll({
                            dispatch,
                            dispatchHighlighted,
                            currentHighlightedIds: highlighted,
                            currentMainObject: mainObject,
                            currentMainObjects: mainObjects,
                        });
                    }
                    break;
                }
                case "Enter":
                case "NumpadEnter": {
                    if (!isProcoreDesign) {
                        return;
                    }
                    confirmCurrentModeChanges();
                    break;
                }
            }

            if (event.key.match(/^[a-z]$/i)) {
                rememberPressedKey(event);
            }
        }

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    });
}

type PressedKey = {
    ctrl: boolean;
    meta: boolean;
    alt: boolean;
    shift: boolean;
    code: string;
};

function pressedKeyIs(pressedKey: PressedKey | null, code: string, modifiers: Partial<Omit<PressedKey, "code">> = {}) {
    if (!pressedKey) {
        return false;
    }
    if (pressedKey.code !== code) {
        return false;
    }
    const modifierKeys = ["ctrl", "meta", "alt", "shift"] as const;
    return modifierKeys.every((m) => pressedKey[m] === (modifiers[m] ?? false));
}
